import { useState, useMemo, useRef } from 'react';
import { useWalletConnect } from '@btc-vision/walletconnect';
import { getContract, JSONRpcProvider } from 'opnet';
import { networks } from '@btc-vision/bitcoin';
import { Campaign } from '../config/campaigns';
import { DonationRecord } from '../config/donations';
import { BITFUND_CONTRACT_ADDRESS, BITFUND_ABI, IBitFundContract } from '../config/contract';

interface MyCampaignsProps {
    readonly campaigns: readonly Campaign[];
    readonly donations: readonly DonationRecord[];
    readonly walletHex: string;
    readonly onClose: () => void;
    readonly onRefresh: () => Promise<void>;
}

function truncateAddr(hex: string): string {
    if (hex.length <= 16) return hex;
    return `${hex.slice(0, 10)}...${hex.slice(-6)}`;
}

export function MyCampaigns({ campaigns, donations, walletHex, onClose, onRefresh }: MyCampaignsProps) {
    const { walletAddress, provider, address } = useWalletConnect();
    const [expandedId, setExpandedId] = useState<bigint | null>(null);
    const [editingId, setEditingId] = useState<bigint | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editDesc, setEditDesc] = useState('');
    const [editMeta, setEditMeta] = useState('');
    const [editStatus, setEditStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
    const [editError, setEditError] = useState('');
    const [deactivatingId, setDeactivatingId] = useState<bigint | null>(null);
    const [deactivateError, setDeactivateError] = useState('');
    const submittingRef = useRef(false);

    const myCampaigns = useMemo(() => {
        if (!walletHex) return [];
        return campaigns.filter(
            (c) => c.creatorAddress.toLowerCase() === walletHex.toLowerCase(),
        );
    }, [campaigns, walletHex]);

    const totalRaised = useMemo(
        () => myCampaigns.reduce((sum, c) => sum + c.raisedBTC, 0),
        [myCampaigns],
    );

    const totalBackers = useMemo(
        () => myCampaigns.reduce((sum, c) => sum + c.backers, 0),
        [myCampaigns],
    );

    const donationsByCampaign = useMemo(() => {
        const map = new Map<string, DonationRecord[]>();
        for (const d of donations) {
            const key = d.campaignId.toString();
            const arr = map.get(key);
            if (arr) {
                arr.push(d);
            } else {
                map.set(key, [d]);
            }
        }
        return map;
    }, [donations]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    };

    const toggleExpand = (id: bigint) => {
        setExpandedId((prev) => (prev === id ? null : id));
    };

    const startEdit = (c: Campaign) => {
        setEditingId(c.id);
        setEditTitle(c.title);
        setEditDesc(c.description);
        setEditMeta(c.metadata);
        setEditStatus('idle');
        setEditError('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditStatus('idle');
        setEditError('');
    };

    const handleSave = async () => {
        if (submittingRef.current || !editingId) return;
        if (!walletAddress || !provider || !address) {
            setEditError('Wallet not connected');
            return;
        }
        if (editTitle.trim().length < 3 || editTitle.trim().length > 80) {
            setEditError('Title must be 3-80 characters');
            return;
        }
        if (editDesc.trim().length < 10 || editDesc.trim().length > 2000) {
            setEditError('Description must be 10-2000 characters');
            return;
        }

        submittingRef.current = true;
        setEditStatus('saving');
        setEditError('');

        try {
            const rpcProvider = provider as JSONRpcProvider;
            const network = networks.opnetTestnet;

            const contract = getContract<IBitFundContract>(
                BITFUND_CONTRACT_ADDRESS,
                BITFUND_ABI,
                rpcProvider,
                network,
                address,
            );

            const sim = await contract.updateCampaign(
                editingId,
                editTitle.trim(),
                editDesc.trim(),
                editMeta.trim(),
            );

            if ('error' in sim && sim.error) {
                throw new Error(String(sim.error));
            }

            const receipt = await sim.sendTransaction({
                signer: null,
                mldsaSigner: null,
                refundTo: walletAddress,
                maximumAllowedSatToSpend: 100_000n,
                network,
            });

            if (!receipt.transactionId) {
                throw new Error('Transaction returned no result');
            }

            setEditStatus('success');
            setTimeout(async () => {
                setEditingId(null);
                setEditStatus('idle');
                await onRefresh();
            }, 2000);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setEditError('Failed: ' + msg);
            setEditStatus('error');
        } finally {
            submittingRef.current = false;
        }
    };

    const handleDeactivate = async (campaignId: bigint) => {
        if (submittingRef.current) return;
        if (!walletAddress || !provider || !address) return;

        submittingRef.current = true;
        setDeactivatingId(campaignId);
        setDeactivateError('');

        try {
            const rpcProvider = provider as JSONRpcProvider;
            const network = networks.opnetTestnet;

            const contract = getContract<IBitFundContract>(
                BITFUND_CONTRACT_ADDRESS,
                BITFUND_ABI,
                rpcProvider,
                network,
                address,
            );

            const sim = await contract.deactivateCampaign(campaignId);

            if ('error' in sim && sim.error) {
                throw new Error(String(sim.error));
            }

            const receipt = await sim.sendTransaction({
                signer: null,
                mldsaSigner: null,
                refundTo: walletAddress,
                maximumAllowedSatToSpend: 100_000n,
                network,
            });

            if (!receipt.transactionId) {
                throw new Error('Transaction returned no result');
            }

            setTimeout(async () => {
                setDeactivatingId(null);
                await onRefresh();
            }, 2000);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setDeactivateError('Failed: ' + msg);
            setDeactivatingId(null);
        } finally {
            submittingRef.current = false;
        }
    };

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal" style={{ maxWidth: 720, maxHeight: '85vh', overflow: 'auto' }}>
                <div className="modal__header">
                    <h2 className="modal__title">My Campaigns</h2>
                    <button className="modal__close" onClick={onClose} aria-label="Close">
                        x
                    </button>
                </div>

                {!walletHex ? (
                    <div className="my-campaigns__empty">
                        <span className="my-campaigns__empty-icon">&#x1F513;</span>
                        <p>Connect your wallet to view your campaigns.</p>
                    </div>
                ) : myCampaigns.length === 0 ? (
                    <div className="my-campaigns__empty">
                        <span className="my-campaigns__empty-icon">&#x1F680;</span>
                        <p>No campaigns yet — create your first!</p>
                    </div>
                ) : (
                    <>
                        <div className="my-campaigns__stats">
                            <div className="my-campaigns__stat">
                                <span className="my-campaigns__stat-value">
                                    {myCampaigns.length}
                                </span>
                                <span className="my-campaigns__stat-label">My Campaigns</span>
                            </div>
                            <div className="my-campaigns__stat">
                                <span className="my-campaigns__stat-value">
                                    {totalRaised.toFixed(4)}
                                </span>
                                <span className="my-campaigns__stat-label">BTC Raised</span>
                            </div>
                            <div className="my-campaigns__stat">
                                <span className="my-campaigns__stat-value">{totalBackers}</span>
                                <span className="my-campaigns__stat-label">Total Backers</span>
                            </div>
                        </div>

                        <div className="my-campaigns__list">
                            {myCampaigns.map((c) => {
                                const isOpen = expandedId === c.id;
                                const isEditing = editingId === c.id;
                                const progress =
                                    c.goalBTC > 0
                                        ? Math.min((c.raisedBTC / c.goalBTC) * 100, 100)
                                        : 0;
                                const campaignDonations =
                                    donationsByCampaign.get(c.id.toString()) ?? [];

                                return (
                                    <div key={c.id.toString()} className="my-campaigns__card">
                                        <div
                                            className="my-campaigns__card-header"
                                            onClick={() => toggleExpand(c.id)}
                                        >
                                            <span className="my-campaigns__card-icon">
                                                {c.icon}
                                            </span>
                                            <span className="my-campaigns__card-title">
                                                {c.title}
                                            </span>
                                            <span className="my-campaigns__badge my-campaigns__badge--category">
                                                {c.category}
                                            </span>
                                            <span
                                                className={`my-campaigns__badge ${c.active ? 'my-campaigns__badge--active' : 'my-campaigns__badge--ended'}`}
                                            >
                                                {c.active ? 'Active' : 'Ended'}
                                            </span>
                                            <svg
                                                className={`my-campaigns__chevron${isOpen ? ' my-campaigns__chevron--open' : ''}`}
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        </div>

                                        {isOpen && (
                                            <div className="my-campaigns__card-body">
                                                {isEditing ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                                                        <div className="input-group">
                                                            <label className="input-group__label">Title</label>
                                                            <input
                                                                className="input-group__field"
                                                                type="text"
                                                                value={editTitle}
                                                                onChange={(e) => setEditTitle(e.target.value)}
                                                                maxLength={80}
                                                                disabled={editStatus === 'saving'}
                                                                style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--font-size-sm)' }}
                                                            />
                                                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                                                                {editTitle.length}/80
                                                            </span>
                                                        </div>
                                                        <div className="input-group">
                                                            <label className="input-group__label">Description</label>
                                                            <textarea
                                                                className="input-group__field"
                                                                value={editDesc}
                                                                onChange={(e) => setEditDesc(e.target.value)}
                                                                maxLength={2000}
                                                                disabled={editStatus === 'saving'}
                                                                style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--font-size-xs)', minHeight: 80, resize: 'vertical' }}
                                                            />
                                                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                                                                {editDesc.length}/2000
                                                            </span>
                                                        </div>
                                                        {editStatus === 'success' && (
                                                            <p style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-sm)', textAlign: 'center' }}>
                                                                Campaign updated! Waiting for block confirmation...
                                                            </p>
                                                        )}
                                                        {editError && (
                                                            <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: 'var(--space-sm)', background: 'rgba(239,68,68,0.1)', borderRadius: 'var(--radius-md)' }}>
                                                                {editError}
                                                            </p>
                                                        )}

                                                        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                                                            <button
                                                                className="btn btn--primary"
                                                                style={{ flex: 1 }}
                                                                onClick={handleSave}
                                                                disabled={editStatus === 'saving' || editStatus === 'success'}
                                                            >
                                                                {editStatus === 'saving' ? 'Confirm in OP_WALLET...' : 'Save Changes'}
                                                            </button>
                                                            <button
                                                                className="btn btn--wallet"
                                                                onClick={cancelEdit}
                                                                disabled={editStatus === 'saving'}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="my-campaigns__progress">
                                                            <div className="my-campaigns__progress-bar">
                                                                <div
                                                                    className="my-campaigns__progress-fill"
                                                                    style={{
                                                                        width: `${progress}%`,
                                                                    }}
                                                                />
                                                            </div>
                                                            <div
                                                                className="progress__info"
                                                                style={{ fontSize: '0.7rem' }}
                                                            >
                                                                <span className="progress__amount">
                                                                    {c.raisedBTC.toFixed(4)} /{' '}
                                                                    {c.goalBTC} BTC
                                                                </span>
                                                                <span>{progress.toFixed(0)}%</span>
                                                            </div>
                                                        </div>

                                                        <div className="my-campaigns__card-stats">
                                                            <span>
                                                                Raised:{' '}
                                                                <strong>
                                                                    {c.raisedBTC.toFixed(4)} BTC
                                                                </strong>
                                                            </span>
                                                            <span>
                                                                Backers: <strong>{c.backers}</strong>
                                                            </span>
                                                            <span>
                                                                Duration:{' '}
                                                                <strong>{c.durationDays}d</strong>
                                                            </span>
                                                        </div>

                                                        <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)' }}>
                                                            <button
                                                                className="btn btn--wallet"
                                                                style={{ fontSize: 'var(--font-size-xs)' }}
                                                                onClick={(e) => { e.stopPropagation(); startEdit(c); }}
                                                            >
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                                </svg>
                                                                Edit Campaign
                                                            </button>
                                                            {c.active && (
                                                                <button
                                                                    className="btn btn--wallet"
                                                                    style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error, #ef4444)' }}
                                                                    onClick={(e) => { e.stopPropagation(); handleDeactivate(c.id); }}
                                                                    disabled={deactivatingId === c.id}
                                                                >
                                                                    {deactivatingId === c.id ? 'Confirm in OP_WALLET...' : 'Deactivate'}
                                                                </button>
                                                            )}
                                                        </div>
                                                        {deactivateError && deactivatingId === null && (
                                                            <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-xs)' }}>
                                                                {deactivateError}
                                                            </p>
                                                        )}

                                                        {campaignDonations.length > 0 ? (
                                                            <>
                                                                <div className="my-campaigns__donations-title">
                                                                    Donations ({campaignDonations.length})
                                                                </div>
                                                                <div className="my-campaigns__donation-table">
                                                                    {campaignDonations.map((d) => (
                                                                        <div
                                                                            key={d.id.toString()}
                                                                            className="my-campaigns__donation-row"
                                                                        >
                                                                            <span className="my-campaigns__donation-donor">
                                                                                {truncateAddr(d.donorAddress)}
                                                                            </span>
                                                                            <span className="my-campaigns__donation-amount">
                                                                                {d.amountBTC.toFixed(4)} BTC
                                                                            </span>
                                                                            <span className="my-campaigns__donation-block">
                                                                                #{d.blockHeight.toString()}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="my-campaigns__no-donations">
                                                                No donations yet
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
